import React from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { ReportExportStatus } from '@prisma/client';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  deleteReportExport,
  fetchReportExportDownloadUrl,
  fetchReportExportsPage,
} from '../../../services/reports';
import { FooterAction, LoadedUser, Row } from '../../../interfaces';
import { ReportExport } from '../../../types/reports';
import ReuseableTable from '../../table/ReuseableTable';
import Badge, { ColorScheme } from '../../global/Badge';
import TableDropdownMenu from '../../global/TableDropdownMenu';
import useConfirm from '../../../hooks/useConfirm';
import useInfiniteQueryPage from '../../../hooks/global/useInfiniteQueryPage';
import useLoadingModal from '../../../hooks/global/useLoadingModal';
import useUser from '../../../hooks/global/useUser';
import {
  formatExportDateRange,
  isExportExpired,
  isExportInFlight,
} from '../../../helpers/reports/exports';
import { EXPORTS_QUERY_KEY } from '../../../constants/reports';
import { downloadFileUrl, isIosDevice } from '../../../utils/global/helper';

const TYPE_LABELS: Record<ReportExport['type'], string> = {
  APPOINTMENTS: 'Appointments',
  ADDITIONAL_SERVICES: 'Services',
  ORDERS: 'Retail Checkouts',
};

const STATUS_LABELS: Record<ReportExportStatus, string> = {
  PENDING: 'Queued',
  PROCESSING: 'Processing',
  READY: 'Ready',
  FAILED: 'Failed',
};

const STATUS_COLOR: Record<ReportExportStatus, ColorScheme> = {
  PENDING: 'grey',
  PROCESSING: 'grey',
  READY: 'secondary',
  FAILED: 'primary',
};

const STATUS_ICON: Record<ReportExportStatus, typeof CheckIcon> = {
  PENDING: AccessTimeIcon,
  PROCESSING: AutorenewIcon,
  READY: CheckIcon,
  FAILED: CloseIcon,
};

export default function ExportsTab() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { user } = useUser<LoadedUser>();
  const businessId = user.business.id;

  const { data, isLoading, hasNextPage, fetchNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: [...EXPORTS_QUERY_KEY, businessId],
      queryFn: ({ pageParam }) =>
        fetchReportExportsPage(businessId, {
          cursor: pageParam ?? null,
          limit: 25,
        }),
      getNextPageParam: page =>
        page.meta.hasMore ? page.meta.cursor : undefined,
      initialPageParam: undefined as string | undefined,
      refetchInterval: query => {
        const all = query.state.data?.pages.flatMap(p => p.result) ?? [];
        return all.some(e => isExportInFlight(e.status)) ? 10_000 : false;
      },
      refetchOnWindowFocus: true,
    });

  const exports = React.useMemo<ReportExport[]>(
    () => data?.pages.flatMap(p => p.result) ?? [],
    [data],
  );

  const {
    page,
    displayedItems,
    shouldShowPagination,
    onPageChange,
    rowsPerPage,
  } = useInfiniteQueryPage({
    data: exports,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    isFetching,
  });

  const onDownload = React.useCallback(
    async (id: string) => {
      try {
        openLoadingModal({
          header: 'Preparing download',
          message: 'Your download is being prepared. Please wait...',
        });
        const { url } = await fetchReportExportDownloadUrl(id);
        downloadFileUrl(url);
        toast.info(
          isIosDevice()
            ? 'Download started. Find the file in the Files app under Downloads, or tap the downloads icon in Safari. If nothing happened, your browser may not support downloads — try opening this page in Safari or on a computer.'
            : "Download started. If you don't see it, check your browser's downloads.",
          { autoClose: 10_000 },
        );
      } catch {
        toast.error('Failed to download export');
      } finally {
        closeLoadingModal();
      }
    },
    [closeLoadingModal, openLoadingModal],
  );

  const { mutate: removeExport, isPending: isDeleting } = useMutation({
    mutationFn: deleteReportExport,
    onMutate: () => {
      openLoadingModal({
        header: 'Deleting export',
        message: 'Your export is being deleted. Please wait...',
      });
    },
    onSettled: () => {
      closeLoadingModal();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPORTS_QUERY_KEY });
    },
    onError: () => {
      toast.error('Failed to delete export');
    },
  });

  const onDelete = React.useCallback(
    async (id: string) => {
      const confirmed = await confirm({
        title: 'Delete export?',
        message:
          'This will remove the export from your list and delete the file. You can always re-run the export from the Reports tab.',
        positiveText: 'Delete',
        negativeText: 'Cancel',
      });
      if (!confirmed) return;
      removeExport(id);
    },
    [confirm, removeExport],
  );

  const rows = React.useMemo<Row[]>(
    () =>
      displayedItems.map(reportExport => {
        const expired = isExportExpired(reportExport);
        const statusLabel = expired
          ? 'Expired'
          : STATUS_LABELS[reportExport.status];
        const statusColor: ColorScheme = expired
          ? 'grey'
          : STATUS_COLOR[reportExport.status];
        const StatusIcon = expired
          ? CloseIcon
          : STATUS_ICON[reportExport.status];
        const canDownload =
          reportExport.status === ReportExportStatus.READY && !expired;
        const actions = [
          canDownload && {
            id: 'download',
            label: 'Download',
            action: () => onDownload(reportExport.id),
          },
          {
            id: 'delete',
            label: 'Delete',
            action: () => onDelete(reportExport.id),
            disabled: isDeleting,
          },
        ].filter(Boolean) as FooterAction[];
        return {
          rowId: reportExport.id,
          cells: [
            TYPE_LABELS[reportExport.type],
            formatExportDateRange(reportExport.filters),
            <Badge
              text={statusLabel}
              icon={StatusIcon}
              colorScheme={statusColor}
              rounded
              sx={{ display: 'inline-flex', width: 'fit-content' }}
            />,
            reportExport.expiresAt
              ? format(new Date(reportExport.expiresAt), 'MMM d, yyyy')
              : '—',
            format(new Date(reportExport.createdAt), 'MMM d, yyyy h:mm a'),
            <TableDropdownMenu options={actions} />,
          ],
        };
      }),
    [displayedItems, isDeleting, onDownload, onDelete],
  );

  return (
    <ReuseableTable
      name="Exports"
      rows={rows}
      headerCells={[
        { id: 'type', label: 'Report' },
        { id: 'range', label: 'Date Range' },
        { id: 'status', label: 'Status' },
        { id: 'expiresAt', label: 'Expires' },
        { id: 'createdAt', label: 'Requested' },
        { id: 'actions', label: '' },
      ]}
      emptyMessage="No exports yet. Use the Download button on a report to start one."
      loading={isLoading}
      pagination={
        shouldShowPagination
          ? {
              count: exports.length,
              rowsPerPage,
              page,
              setPage: onPageChange,
              hasNext: hasNextPage,
              hasPrevious: page > 0,
            }
          : undefined
      }
    />
  );
}
